import { useEffect, useState } from 'react';
import {
  getFallbackProductRecords,
  loadProductRecordsForAdmin,
  loadPublishedProductRecords,
  type ProductRecord,
} from '../lib/productRecords';

type UseProductRecordsOptions = {
  admin?: boolean;
};

export function useProductRecords(options: UseProductRecordsOptions = {}) {
  const { admin = false } = options;
  const [products, setProducts] = useState<ProductRecord[]>(() => getFallbackProductRecords());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError(null);

      if (admin) {
        const response = await loadProductRecordsForAdmin();
        if (!active) return;
        if (response.error) {
          setError(
            response.error && typeof response.error === 'object' && 'message' in response.error
              ? String((response.error as { message?: unknown }).message ?? 'Failed to load products')
              : 'Failed to load products'
          );
        }
        setProducts(response.draft);
      } else {
        const response = await loadPublishedProductRecords();
        if (!active) return;
        if (response.error) {
          setError(
            response.error && typeof response.error === 'object' && 'message' in response.error
              ? String((response.error as { message?: unknown }).message ?? 'Failed to load products')
              : 'Failed to load products'
          );
        }
        setProducts(response.products);
      }
      setLoading(false);
    }

    void load();

    return () => {
      active = false;
    };
  }, [admin]);

  return {
    products,
    loading,
    error,
  };
}
